import React, { useState, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, AppBar, Toolbar, IconButton, Typography, Collapse, Tooltip, Link, Button } from '@mui/material';
import { Menu, Bell, Home, MapPin, ChevronRight, CoinsIcon } from 'lucide-react';
import BroadcastBanner from './shared/BroadCast/BroadCastBanner';
import AppHeaderDropdown from './header/AppHeaderDropdownNew';
import { UserContext } from 'src/Contexts/UserContext';
import { activeTabCodeGiver } from 'src/utils/activeTabCodeGiver';
import useBreakpoints from 'src/utils/Breakpoints';
import SavingsIcon from '@mui/icons-material/Savings';
import { CK_Dsh_Gujmarg } from 'src/constants/api.config';


function DepartmentTitle({ breakpoints }) {
  const { isSm, isMd, isLg } = breakpoints
  if (isSm) return null;
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      borderRight: '2px solid rgba(0,0,0,0.1)',

      pr: 2,
      mr: 0
    }}>
      <Box sx={{
        mr: 1.5,
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: 'scale(1.05)'
        }
      }}>
        <img loading="lazy"
          src={process.env.PUBLIC_URL + "/emblem_small.jpg"}
          alt="emblemImage"
          style={{
            width: 'auto',
            height: '35px',
          }}
        />
      </Box>
      <Typography sx={{
        fontSize: '1.25rem',
        fontWeight: 600,
        color: 'rgb(73, 80, 87)',
        letterSpacing: '0.3px',
        m: 0,
        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {isLg ? " Roads and Buildings Department" : "R&BD "}
      </Typography>
    </Box>
  );
}

function PageTitle({ title, breakpoints }) {
  const history = useHistory();
  const location = useLocation();
  const { isLg, isMd, isSm } = breakpoints;

  const getDynamicFontSizeForSm = (text1 = '', text2 = '') => {
    const totalLength = (text1?.length || 0) + (text2?.length || 0);
    if (totalLength >= 46) return '0.7rem';
    if (totalLength >= 45) return '0.9rem';
    if (totalLength >= 15) return '0.9rem';
    return '0.9rem';
  };

  const fontSize = isSm
    ? getDynamicFontSizeForSm(title?.parentTitle, title?.childTitle)
    : (isLg ? '1.1rem' : '0.9rem');

  const navigateToParent = () => {
    if (title?.parentTitle && location.pathname !== "/home") {
      history.push("/home");
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: isLg ? 2 : 0,
        position: 'relative',
        minWidth: 0,
        // maxWidth: isSm ? '60vw' : '100%',
        flex: 1,
        overflow: 'hidden',

      }}
    >
      {title?.parentTitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(45deg, rgba(248,250,252,0) 0%, rgba(248,250,252,0.8) 50%, rgba(248,250,252,0) 100%)',
            borderRadius: '4px',
            px: isSm ? 0 : 1.5,
            py: 0.5,
            transition: 'all 0.3s ease',
            minWidth: 0,
            // maxWidth: isSm ? '60vw' : '100%',
            // overflow: 'hidden',
            '&:hover': title.parentTitle ? {
              background: 'rgba(79, 70, 229, 0.05)',
              transform: 'translateY(-1px)',
              cursor: 'pointer'
            } : {}
          }}
          onClick={navigateToParent}
        >
          <Typography
            sx={{
              fontSize: fontSize,
              fontWeight: 550,
              color: 'rgb(73, 80, 87)',
              letterSpacing: isSm ? "0px" : '0.5px',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // maxWidth: isSm ? '30vw' : '100%',
              '&:hover': title.parentTitle ? {
                color: 'rgb(91, 76, 226)',
              } : {}
            }}
          >
            {title?.parentTitle || ''}
          </Typography>
          <ChevronRight
            size={isLg || isMd ? 20 : 16}
            style={{
              color: 'rgba(79, 70, 229, 0.5)',
              marginRight: isSm ? "0px" : '8px',
              marginLeft: isSm ? "0px" : '8px',
              transition: 'all 0.3s ease',
              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))'
            }}
          />
        </Box>
      )}

      {title?.childTitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minWidth: 0,
            // maxWidth: isSm ? '60vw' : '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'rgba(79, 70, 229, 0.08)',
              borderRadius: '4px',
              px: isSm ? 0.5 : 1.5,
              py: 0.5,
              boxShadow: '0 2px 4px rgba(79, 70, 229, 0.05)',
              position: 'relative',
              overflow: 'hidden',
              minWidth: 0,
              // maxWidth: isSm ? '60vw' : '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: fontSize,
                fontWeight: 500,
                color: 'rgb(91, 76, 226)',
                letterSpacing: '0.5px',
                textShadow: '0 1px 1px rgba(255,255,255,1)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                // maxWidth: isSm ? '100%' : '100%',
              }}
            >
              {title?.childTitle || ''}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

const AppHeader = ({ isDetails, title, sideBarFlag, setSideBarFlag, currentMessage }) => {
  const { showBanner, setShowBanner, contextUserData } = useContext(UserContext);
  const currentLoginId = contextUserData?.LoginUserID?.trim() || "sec-rnb";
  const location = useLocation();
  const headingInfo = PageHeadingGiver(location.pathname.trim());
  const rawTitle = headingInfo?.childTitle || "";
  const upperTitle = rawTitle.toUpperCase();

  // 2. Identify the target system
  let targetSystem = null;
  if (upperTitle.includes("STROBES")) targetSystem = "STROBES";
  if (upperTitle.includes("PANCHAYAT")) targetSystem = "Panchayat";
  if (upperTitle.includes("GUJMARG")) targetSystem = "GujMARG";
  console.log("see the upper title", upperTitle)
  console.log(upperTitle.includes("GUJMARG"))
  // 3. Extract the User ID from context
  const loginId = contextUserData?.LoginUserID?.trim() || "sec-rnb";

  const history = useHistory();
  const { isSm, isMd, isLg } = useBreakpoints();
  const hasMessages = currentMessage && currentMessage.length > 0;


  const handleMotherLinkClick = async () => {
    const headingInfo = PageHeadingGiver(location.pathname.trim());
    const upperTitle = (headingInfo?.childTitle || "").toUpperCase();
    const loginId = contextUserData?.LoginUserID?.trim() || "sec-rnb";
    console.log("logged user data", loginId, contextUserData)
    if (upperTitle.includes("STROBES")) {
      // Open tab immediately to avoid popup blockers
      const newTab = window.open("about:blank", "_blank");

      try {
        const response = await fetch('https://strobes.in/api/auth/thirdPartyLogin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ "thirdparty_username": loginId })
        });

        const data = await response.json();
        console.log(data)
        if (data) {
          const form = newTab.document.createElement('form');
          form.method = 'POST';
          form.action = 'https://strobes.in/postlogin';

          const params = {
            username: data.data.username,
            request_from: "rnbdashboard.com",
            thirdparty_username: loginId,
          };

          Object.entries(params).forEach(([k, v]) => {
            const input = newTab.document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = v;
            form.appendChild(input);
          });

          newTab.document.body.appendChild(form);
          form.submit();
        } else {
          newTab?.close();
        }
      } catch (e) {
        console.error("SSO Error:", e);
        newTab?.close();
      }
    }
    else if (upperTitle.includes("PANCHAYAT")) {
      window.open(`https://182.72.220.203/MMGSY_New/DashBoard.aspx?username=${loginId}`, "_blank");
    }
    else if (upperTitle.includes("GUJMARG")) {
      window.open(CK_Dsh_Gujmarg, "_blank");
    }
  };


  function PageHeadingGiver(path) {

    if (contextUserData?.UserRights?.length > 0) {
      for (let right of contextUserData.UserRights) {
        const child = right.children?.find((child) => {
          return child.formobjname?.includes(path);
        });
        if (child) {
          return { childTitle: child.formtitle, parentTitle: right.formtitle };
        }
      }
    }
    return "";
  }
  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'white',
        boxShadow: '0 4px 20px rgba(79, 70, 229, 0.15)',
        zIndex: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none'
        },
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'space-between', // Always use space-between instead of flex-start for isSm
        minHeight: isSm ? '56px !important' : '72px !important',
        px: isLg ? (sideBarFlag ? 3 : 2) : 0,
        pl: isLg ? undefined : '5px !important',
        pr: isLg ? undefined : '5px !important',
        width: '100%',
        boxShadow: 'none',
        borderBottom: hasMessages && showBanner
          ? '1px solid rgba(0,0,0,0.08)'
          : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flex: '1', // Allow this box to grow and take available space
            minWidth: 0, // Prevent overflow issues
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: 'blur(8px)',
            borderRadius: '6px',
            p: isLg ? 0.5 : 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.3) 100%)',
          }}
        >
          {!isLg && (
            <IconButton
              onClick={() => setSideBarFlag(!sideBarFlag)}
              sx={{
                color: '#4f46e5',
                marginRight: '0rem',
                px: isSm ? 0 : 1,
                pr: isSm ? 0.2 : 1,

                flexShrink: 0, // Prevent shrinking
              }}
            >
              <Menu size={isSm ? 18 : 20} />
            </IconButton>
          )}
          {isLg && <DepartmentTitle breakpoints={{ isSm, isMd, isLg }} />}
          <PageTitle title={PageHeadingGiver(location.pathname.trim())} breakpoints={{ isSm, isMd, isLg }} />

          {targetSystem && (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {/* <h6  style={{color:'black' , margin:'0'}}>Click</h6> */}

              <Tooltip
                title={`Click here to go to ${targetSystem}`}
                arrow
                placement="top"
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleMotherLinkClick}
                  endIcon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="#FFD700">
                      <ellipse cx="12" cy="6" rx="8" ry="3" />
                      <path d="M4 6v4c0 1.66 3.58 3 8 3s8-1.34 8-3V6c0 1.66-3.58 3-8 3S4 7.66 4 6z" />
                      <path d="M4 10v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3S4 11.66 4 10z" />
                      <path d="M4 14v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3S4 15.66 4 14z" />
                    </svg>
                  }
                  sx={{
                    ml: 1,
                    height: '32px',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#FF0000',
                    fontFamily: `Sarabun, Arial, sans-serif`,
                    fontSize: '16px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    '&:hover': {
                      backgroundColor: '#e55353e5',
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                    }
                  }}
                >
                  Click Here For Mother Login
                </Button>

              </Tooltip>
              {/* <h6  style={{color:'black' , margin:'0', marginLeft:'8px'}}> to View More Details</h6> */}

            </div>
          )}    </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0, // Prevent this box from shrinking
          ml: 1 // Add some margin to separate from the left content
        }}>
          {!isSm && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {location.pathname?.trim() !== "/home" && <Box sx={{ position: 'relative' }}>
              <Tooltip title="Go to Home" placement="bottom">
                <IconButton
                  sx={{
                    color: '#4f46e5',
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(79, 70, 229, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(79, 70, 229, 0.2)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => {
                    history.push("/home")
                  }}
                >
                  <Home size={isLg ? 24 : isMd ? 20 : 18} />
                </IconButton>
              </Tooltip>
            </Box>}

            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Notifications" placement="bottom">
                <IconButton
                  onClick={() => {
                    const tempShowBanner = !showBanner;
                    setShowBanner(tempShowBanner);
                    localStorage.setItem('showBanner', tempShowBanner);
                  }}
                  sx={{
                    color: '#4f46e5',
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(79, 70, 229, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(79, 70, 229, 0.2)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Bell size={isLg ? 24 : isMd ? 20 : 18} />
                </IconButton>
              </Tooltip>

              {hasMessages && !showBanner && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#c62828',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(198, 40, 40, 0.7)'
                    },
                    '70%': {
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 8px rgba(198, 40, 40, 0)'
                    },
                    '100%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(198, 40, 40, 0)'
                    }
                  }
                }} />
              )}
            </Box>
          </Box>}

          <AppHeaderDropdown
            // avatarSrc={avatarSrc}
            currentMessage={currentMessage}
          />
        </Box>
      </Toolbar>

      {hasMessages && (
        <Collapse
          in={showBanner}
          timeout={300}
          easing={{
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          sx={{
            width: '100%',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -5,
              left: 0,
              right: 0,
              height: 10,
              zIndex: -1,
              transition: 'all 0.3s ease'
            }
          }}
        >
          <Box
            component="div"
            sx={{
              width: '100%',
              background: 'white',
              transition: 'all 0.3s ease'
            }}
          >
            <BroadcastBanner currentMessage={currentMessage} />
          </Box>
        </Collapse>
      )}
    </AppBar>
  );
};

export default AppHeader;